import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { browserSessionPersistence, setPersistence } from 'firebase/auth';
import { auth } from '../../../shared/storage/firebase';
import { StudentAuthService } from '../../../shared/storage/StudentAuthService';

type LaunchState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; redirectTo: string };

const StudentLaunch: React.FC = () => {
  const location = useLocation();
  const [state, setState] = useState<LaunchState>({ status: 'loading' });

  const params = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return {
      childId: sp.get('childId') || '',
      squadId: sp.get('squadId') || '',
      childName: sp.get('childName') || 'Student',
    };
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!params.childId || !params.squadId) {
        setState({
          status: 'error',
          message: 'Missing student launch parameters. Please close this window and try again.',
        });
        return;
      }

      try {
        // Isolate auth state to this popup tab/window so we don't replace the parent session.
        await setPersistence(auth, browserSessionPersistence);

        await StudentAuthService.authenticateStudent(params.childId, params.squadId);

        if (cancelled) return;
        setState({ status: 'ready', redirectTo: '/app/games' });
      } catch (e) {
        console.error('[StudentLaunch] Failed to authenticate student:', e);
        if (cancelled) return;
        setState({
          status: 'error',
          message: `Could not launch ${params.childName}'s account. You can close this window and try again.`,
        });
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [params.childId, params.squadId, params.childName]);

  if (state.status === 'ready') {
    return <Navigate to={state.redirectTo} replace />;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 560 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{state.status === 'loading' ? '🎵' : '⚠️'}</div>
        <h2 style={{ margin: 0, marginBottom: 8 }}>
          {state.status === 'loading' ? `Launching ${params.childName}'s Account...` : 'Could not launch account'}
        </h2>
        <p style={{ margin: 0, opacity: 0.95 }}>
          {state.status === 'loading'
            ? 'Please wait while we set up the student session.'
            : state.message}
        </p>
        <div style={{ marginTop: 16, fontSize: 14, opacity: 0.9 }}>
          You can close this window when finished.
        </div>
      </div>
    </div>
  );
};

export default StudentLaunch;

